import Vue from "vue";

const topEndMessage = {
  toast: true,
  position: "top-end",
  showConfirmButton: false,
  timer: 3000,
  timerProgressBar: true,
  didOpen: (toast) => {
    toast.addEventListener("mouseenter", Vue.swal.stopTimer);
    toast.addEventListener("mouseleave", Vue.swal.resumeTimer);
  },
};

const formatDirectory = (dir) => ({
  _id: dir._id,
  name: dir.name,
  children: [],
});

const formatFile = (file) => ({
  _id: file._id,
  name: file.name,
  type: file.type,
  viewCount: file.viewCount,
});

const formatChildren = (children) => {
  const directories = children.directories.map(formatDirectory);
  const files = children.files.map(formatFile);
  return [...directories, ...files];
};

const sortTree = (items) => {
  const sorted = [
    ...items
      .filter((i) => !i.type)
      .sort((a, b) => a.name.toLowerCase().localeCompare(b.name.toLowerCase())),
    ...items
      .filter((i) => !!i.type)
      .sort((a, b) => a.name.toLowerCase().localeCompare(b.name.toLowerCase())),
  ];

  items.splice(0, items.length, ...sorted);
};

export default {
  namespaced: true,
  state: {
    treeItems: [],
  },
  mutations: {
    SET_TREE_ITEMS(state, items) {
      state.treeItems = items;
      sortTree(items);
    },
    ADD_CHILDREN(state, { parentId, children }) {
      const findAndAttach = (items) => {
        for (let item of items) {
          if (item._id === parentId) {
            item.children = children;
            sortTree(children);
            return true;
          }
          if (item.children && findAndAttach(item.children)) return true;
        }
        return false;
      };
      findAndAttach(state.treeItems);
    },
    CREATE_DIRECTORY(state, { parentId, directory }) {
      const findAndAdd = (items) => {
        for (let item of items) {
          if (item._id === parentId) {
            item.children.push(directory);
            sortTree(items);
            return true;
          }
          if (item.children && findAndAdd(item.children)) return true;
        }
        return false;
      };
      findAndAdd(state.treeItems);
    },
    CREATE_FILE(state, { parentId, file }) {
      const findAndAdd = (items) => {
        for (let item of items) {
          if (item._id === parentId) {
            item.children.push(file);
            sortTree(items);
            return true;
          }
          if (item.children && findAndAdd(item.children)) return true;
        }
        return false;
      };
      findAndAdd(state.treeItems);
    },
    MODIFY_DIRECTORY(state, { directory, newParent }) {
      if (newParent) {
        const removeFromTree = (items, directoryId) => {
          for (let i = 0; i < items.length; i++) {
            if (items[i]._id === directoryId) {
              items.splice(i, 1);
              return true;
            }
            if (
              items[i].children &&
              removeFromTree(items[i].children, directoryId)
            ) {
              return true;
            }
          }
          return false;
        };
        const addToParent = (items, parentId, directory) => {
          for (let item of items) {
            if (item._id === parentId) {
              item.children.push(directory);
              sortTree(item.children);
              return true;
            }
            if (
              item.children &&
              addToParent(item.children, parentId, directory)
            ) {
              return true;
            }
          }
          return false;
        };

        removeFromTree(state.treeItems, directory._id);
        addToParent(state.treeItems, directory.parent, directory);
      }

      const findAndModify = (items) => {
        for (let item of items) {
          if (item._id === directory._id) {
            item.name = directory.name;
            item.visibility = directory.visibility;
            sortTree(items);
            return true;
          }
          if (item.children && findAndModify(item.children)) return true;
        }
        return false;
      };
      findAndModify(state.treeItems);
    },
    MODIFY_FILE(state, { file, newParent }) {
      if (newParent) {
        const removeFromTree = (items, fileId) => {
          for (let i = 0; i < items.length; i++) {
            if (items[i]._id === fileId) {
              items.splice(i, 1);
              return true;
            }
            if (
              items[i].children &&
              removeFromTree(items[i].children, fileId)
            ) {
              return true;
            }
          }
          return false;
        };

        const addToParent = (items, parentId, file) => {
          for (let item of items) {
            if (item._id === parentId) {
              item.children.push(formatFile(file));
              sortTree(item.children);
              return true;
            }
            if (item.children && addToParent(item.children, parentId, file)) {
              return true;
            }
          }
          return false;
        };

        removeFromTree(state.treeItems, file._id);
        addToParent(state.treeItems, file.parent, file);
      }

      const findAndModify = (items) => {
        for (let item of items) {
          if (item._id === file._id) {
            item.name = file.name;
            item.visibility = file.visibility;
            sortTree(items);
            return true;
          }
          if (item.children && findAndModify(item.children)) return true;
        }
        return false;
      };
      findAndModify(state.treeItems);
    },
    DELETE_DIRECTORY(state, directoryId) {
      const findAndDelete = (items) => {
        for (let item of items) {
          if (item._id === directoryId) {
            const index = items.indexOf(item);
            if (index !== -1) items.splice(index, 1);
            return true;
          }
          if (item.children && findAndDelete(item.children)) return true;
        }
        return false;
      };
      findAndDelete(state.treeItems);
    },
    DELETE_FILE(state, fileId) {
      const findAndDelete = (items) => {
        for (let item of items) {
          if (item._id === fileId) {
            const index = items.indexOf(item);
            if (index !== -1) items.splice(index, 1);
            return true;
          }
          if (item.children && findAndDelete(item.children)) return true;
        }
        return false;
      };
      findAndDelete(state.treeItems);
    },
  },

  actions: {
    getRootItems({ commit }) {
      return new Promise((resolve, reject) => {
        Vue.axios
          .get("leodata/directory/root/content")
          .then((response) => {
            const directory = response.data.directory;
            let children = formatChildren(directory.children);
            commit("SET_TREE_ITEMS", children);
            resolve();
          })
          .catch((error) => {
            reject(error);
          });
      });
    },
    fetchChildren({ commit }, parentId) {
      return new Promise((resolve, reject) => {
        Vue.axios
          .get(`leodata/directory/${parentId}/content`)
          .then((response) => {
            const directory = response.data.directory;
            let children = formatChildren(directory.children);
            commit("ADD_CHILDREN", { parentId, children });
            resolve(children);
          })
          .catch((error) => {
            reject(error);
          });
      });
    },
    getDirectoryDetails(_, directoryId) {
      return new Promise((resolve, reject) => {
        Vue.axios
          .get(`leodata/directory/${directoryId}/details`)
          .then((response) => {
            const directory = response.data.directory;
            resolve(directory);
          })
          .catch((error) => {
            reject(error);
          });
      });
    },
    getDirectoryContent(_, directoryId) {
      return new Promise((resolve, reject) => {
        Vue.axios
          .get(`leodata/directory/${directoryId}/content`)
          .then((response) => {
            const directory = response.data.directory;
            resolve(directory);
          })
          .catch((error) => {
            reject(error);
          });
      });
    },
    createDirectory({ commit }, payload) {
      return new Promise((resolve, reject) => {
        Vue.axios
          .post("leodata/directory", payload)
          .then((response) => {
            const parentId = payload.parent;
            const directory = formatDirectory(response.data.directory);
            commit("CREATE_DIRECTORY", { parentId, directory });

            Vue.swal.mixin(topEndMessage).fire({
              icon: "success",
              title: "Dossier créé avec succès",
            });

            resolve();
          })
          .catch((err) => {
            Vue.swal.fire({
              icon: "error",
              title: "Oops...",
              text: "Quelque chose c'est mal passé 🤔",
              footer:
                "Si le problème persiste contacte LéoLearning avec le code: 26",
            });
            reject(err);
          });
      });
    },
    createFile({ commit }, { type, data }) {
      return new Promise((resolve, reject) => {
        if (type === "pdf") {
          Vue.axios
            .post(`leodata/file`, data, {
              headers: {
                withCredentials: true,
                baseURL: "https://api.leolearning.fr/",
                "Content-Type": "multipart/form-data",
              },
            })
            .then((response) => {
              const fileNotFormated = response.data.file;
              const file = formatFile(fileNotFormated);
              commit("CREATE_FILE", {
                parentId: fileNotFormated.parent,
                file,
              });
              Vue.swal.mixin(topEndMessage).fire({
                icon: "success",
                title: "Le fichier a bien été créé",
              });

              resolve(file);
            })
            .catch((error) => {
              console.error(
                `Erreur lors de la création du fichier:`,
                error.response?.data || error.message
              );
              reject(error);
            });
        } else if (type === "youtube") {
          Vue.axios
            .post("leodata/file", data)
            .then((response) => {
              const fileNotFormated = response.data.file;
              const file = formatFile(fileNotFormated);
              commit("CREATE_FILE", {
                parentId: fileNotFormated.parent,
                file,
              });
              Vue.swal.mixin(topEndMessage).fire({
                icon: "success",
                title: "Le fichier a bien été créé",
              });

              resolve(file);
            })
            .catch((err) => {
              Vue.swal.fire({
                icon: "error",
                title: "Oops...",
                text: "Quelque chose c'est mal passé 🤔",
                footer:
                  "Si le problème persiste contacte LéoLearning avec le code: 26",
              });
              reject(err);
            });
        }
      });
    },
    modifyDirectory({ commit }, { directoryId, payload }) {
      return new Promise((resolve, reject) => {
        Vue.axios
          .patch(`leodata/directory/${directoryId}`, payload)
          .then((response) => {
            const directory = formatDirectory(response.data.directory);
            const newParent = response.data.newParent;
            commit("MODIFY_DIRECTORY", { directory, newParent });

            Vue.swal.mixin(topEndMessage).fire({
              icon: "success",
              title: "Dossier modifié avec succès",
            });

            resolve();
          })
          .catch((err) => {
            Vue.swal.fire({
              icon: "error",
              title: "Oops...",
              text: "Quelque chose c'est mal passé 🤔",
              footer:
                "Si le problème persiste contacte LéoLearning avec le code: 26",
            });
            reject(err);
          });
      });
    },
    getFile(_, fileId) {
      return new Promise((resolve, reject) => {
        Vue.axios
          .get(`leodata/file/${fileId}`)
          .then((response) => {
            resolve(response.data);
          })
          .catch((err) => {
            Vue.swal.fire({
              icon: "error",
              title: "Oops...",
              text: "Quelque chose c'est mal passé 🤔",
              footer:
                "Si le problème persiste contacte LéoLearning avec le code: 26",
            });
            reject(err);
          });
      });
    },
    getFileDetails(_, fileId) {
      return new Promise((resolve, reject) => {
        Vue.axios
          .get(`leodata/file/${fileId}/details`)
          .then((response) => {
            resolve(response.data.file);
          })
          .catch((err) => {
            Vue.swal.fire({
              icon: "error",
              title: "Oops...",
              text: "Quelque chose c'est mal passé 🤔",
              footer:
                "Si le problème persiste contacte LéoLearning avec le code: 26",
            });
            reject(err);
          });
      });
    },
    modifyFile({ commit }, { fileId, payload }) {
      return new Promise((resolve, reject) => {
        Vue.axios
          .patch(`leodata/file/${fileId}`, payload)
          .then((response) => {
            const file = response.data.file;
            const newParent = response.data.newParent;
            commit("MODIFY_FILE", { file, newParent });

            Vue.swal.mixin(topEndMessage).fire({
              icon: "success",
              title: "Fichier modifié avec succès",
            });

            resolve();
          })
          .catch((err) => {
            Vue.swal.fire({
              icon: "error",
              title: "Oops...",
              text: "Quelque chose c'est mal passé 🤔",
              footer:
                "Si le problème persiste contacte LéoLearning avec le code: 26",
            });
            reject(err);
          });
      });
    },
    deleteDirectory({ commit }, directoryId) {
      return new Promise((resolve, reject) => {
        Vue.axios
          .delete(`leodata/directory/${directoryId}`)
          .then(() => {
            commit("DELETE_DIRECTORY", directoryId);

            Vue.swal.mixin(topEndMessage).fire({
              icon: "success",
              title: "Dossier supprimé avec succès",
            });

            resolve();
          })
          .catch((err) => {
            Vue.swal.fire({
              icon: "error",
              title: "Oops...",
              text: "Quelque chose c'est mal passé 🤔",
              footer:
                "Si le problème persiste contacte LéoLearning avec le code: 26",
            });
            reject(err);
          });
      });
    },
    deleteFile({ commit }, fileId) {
      return new Promise((resolve, reject) => {
        Vue.axios
          .delete(`leodata/file/${fileId}`)
          .then(() => {
            commit("DELETE_FILE", fileId);

            Vue.swal.mixin(topEndMessage).fire({
              icon: "success",
              title: "Fichier supprimé avec succès",
            });

            resolve();
          })
          .catch((err) => {
            Vue.swal.fire({
              icon: "error",
              title: "Oops...",
              text: "Quelque chose c'est mal passé 🤔",
              footer:
                "Si le problème persiste contacte LéoLearning avec le code: 26",
            });
            reject(err);
          });
      });
    },
    import(_, { directoryId, formData }) {
      return new Promise((resolve, reject) => {
        Vue.axios
          .post(`leodata/import/${directoryId}`, formData, {
            headers: {
              "Content-Type": "multipart/form-data",
            },
          })
          .then(() => {
            // const fileNotFormated = response.data.file;
            // const file = formatFile(fileNotFormated);
            // commit("CREATE_FILE", {
            //   parentId: fileNotFormated.parent,
            //   file,
            // });
            // Vue.swal.mixin(topEndMessage).fire({
            //   icon: "success",
            //   title: "Le fichier a bien été créé",
            // });
            // resolve(file);
          })
          .catch((error) => {
            console.error(
              `Erreur lors de la création du fichier:`,
              error.response?.data || error.message
            );
            reject(error);
          });
      });
    },
  },

  getters: {
    getTreeItems(state) {
      return state.treeItems;
    },
    getTreeItemsForMoving(state) {
      return state.treeItemsForMoving;
    },
  },
};
